/**
 * Shared Products finance hub state and handlers (used by hub section + embedded card rows).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notification } from 'antd';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import designServices from '../../../../services/oneButtonServices/designServices';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import packingSlipService from '../../../../services/packingSlipService';
import { useViewerStore } from '../../../../store/store';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import type { HubGridRow } from '../../../../@types/oneButtonType';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import {
    evaluateDesignLossesMismatchForHubSave,
    enfinPartnerAllowedForDesignLosses,
    ENFIN_PARTNER_REQUIRES_ENFIN_LOSSES_MESSAGE,
    showDesignLossesMismatchModal,
} from './designLossesFinanceGate';
import {
    PRODUCT_DISPLAY_ORDER,
    isRoofFamily,
    isLightreach,
    isSunnova,
    isGoodLeap,
    roofProductCodeForC4,
    rowFromApi,
    matchProductName,
    buildOpportunityProductSavePayload,
    mergeSavedProductsIntoOpportunityDetails,
    validateProductFinanceForSave,
    hubRowHasUnsavedChanges,
    hubRowHasSavedFinancePartner,
    hubRowsLookLikePlaceholders,
    savedOpportunityProductsFingerprint,
    solarFinancePartnerChanged,
    isEnfin,
    isStorageFinanceMethod,
    stripStorageFromFinanceMethod,
    resolveLightreachBatteryMethodOptions,
} from './productsFinanceHubHelpers';

import type {
    PartnerChecklistModalState,
    ProductsFinanceHubContextValue,
} from './ProductsFinanceHubContext.types';

export interface UseProductsFinanceHubControllerArgs {
    salesForceId: string | null | undefined;
}

export function useProductsFinanceHubController({
    salesForceId,
}: UseProductsFinanceHubControllerArgs): ProductsFinanceHubContextValue {
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const setOpportunityDetails = useOneButtonStore((s) => s.setOpportunityDetails);
    const setLeadSyncFormOpen = useOneButtonStore((s) => s.setLeadSyncFormOpen);
    const setSolarUtilityData = useOneButtonStore((s) => s.setSolarUtilityData);
    const setFinanceSelectionRefreshTrigger = useOneButtonStore((s) => s.setFinanceSelectionRefreshTrigger);
    const productsModalOpen = useOneButtonStore((s) => s.commerceHubProductsOpen);
    const productTypeList = useOneButtonStore((s) => s.productTypeList);
    const financePartnersList = useOneButtonStore((s) => s.financePartnersList);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const opportunityLoadError = useOneButtonStore((s) => s.opportunityLoadError);
    const isOpportunityInitializing = useOneButtonStore((s) => s.isOpportunityInitializing);
    const systemDesignLosses = useOneButtonStore((s) => s.systemDesignLosses);
    const pciLockOpportunityProducts = useOneButtonStore((s) => s.pciLockOpportunityProducts);

    const dynamicProductOrder = useMemo(() => {
        const fallback = ['Solar', 'Roof', 'Battery', 'RR', 'RRR'];
        if (!productTypeList?.length) return fallback;
        
        // Sort based on predefined display order
        return [...productTypeList]
            .sort((a, b) => PRODUCT_DISPLAY_ORDER.indexOf(a.Text) - PRODUCT_DISPLAY_ORDER.indexOf(b.Text))
            .map(p => p.Text);
    }, [productTypeList]);

    const partnerOptions = useMemo(() => {
        return (financePartnersList || []).map((p: any) => ({
            value: p.Text,
            label: p.Text,
        }));
    }, [financePartnersList]);

    const [hubOpen, setHubOpen] = useState(true);
    const [rows, setRows] = useState<HubGridRow[]>([]);
    const [noticeAPI, noticeContext] = notification.useNotification({ maxCount: 4, stack: false });
    const [validationError, setValidationError] = useState<string | null>(null);
    const [validationErrorsByProduct, setValidationErrorsByProduct] = useState<Record<string, string>>({});
    const [saveLoadingProduct, setSaveLoadingProduct] = useState<string | null>(null);

    const [goodLeapExistingApp , setGoodLeapExistingApp] = useState<any |null>(null);


    const isOHIO = useMemo(() => {
        const state = String(opportunityDetails?.InstallationAddress?.State || '').toLowerCase();
        return state === 'ohio';
    }, [opportunityDetails]);

    const isGoodLeapApproved = useMemo(() => {
        return !!(opportunityDetails?.IsGoodLeapApproved || opportunityDetails?.isGoodLeapApproved);
    }, [opportunityDetails]);

    const savedOpportunityProducts = useMemo(() => {
        const raw = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        return Array.isArray(raw) ? raw : [];
    }, [opportunityDetails]);

    const hasUnsavedChanges = useMemo(
        () => rows.some((row) => hubRowHasUnsavedChanges(row, savedOpportunityProducts)),
        [rows, savedOpportunityProducts],
    );

    const hasProductUnsavedChanges = useCallback(
        (productName: string) => {
            const row = rows.find((r) => r.product_Type_Name === productName);
            if (!row) return false;
            return hubRowHasUnsavedChanges(row, savedOpportunityProducts);
        },
        [rows, savedOpportunityProducts],
    );

    const hasSavedFinancePartnerForProduct = useCallback(
        (productName: string) => {
            const row = rows.find((r) => r.product_Type_Name === productName);
            return hubRowHasSavedFinancePartner(row, savedOpportunityProducts);
        },
        [rows, savedOpportunityProducts],
    );

    const getCapabilityValue = useCallback(
        (name: string) => getOneButtonCapabilityValue(onebuttonCapabilities, name),
        [onebuttonCapabilities]
    );

    const isOpportunityInfoReadOnly = useMemo(() => {
        // Parity with Angular: getCapability('Opportunity_Information_Access') === 'View'
        if (pciLockOpportunityProducts) return true;
        return getCapabilityValue('Opportunity_Information_Access') === 'View';
    }, [getCapabilityValue, pciLockOpportunityProducts]);

    const isOpportunityInfoDisabled = useMemo(() => {
        const val = getCapabilityValue('Opportunity_Information_Access');
        return val !== 'Edit' && val !== 'View';
    }, [getCapabilityValue]);

    const leadSource = useMemo(() => {
        return String(
            opportunityDetails?.OpportunityDetails?.LeadSource ??
                opportunityDetails?.OpportunityHighlight?.LeadSource ??
                ''
        ).trim();
    }, [opportunityDetails]);

    /** Mirrors Angular `ProductSectionComponent.TCDLightReachFinancePartnerDropdownLogic` */
    const tcdLightReachFinancePartnerDropdownLogic = useMemo(() => {
        const customers = (opportunityDetails as any)?.Customers;
        if (!Array.isArray(customers)) return true;
        const primary = customers.find((c: any) => c?.IsPrimaryContact === true);
        if (!primary) return true;
        const list = primary?.financePartnerCreditStatusList?.CreditStatusList;
        if (!Array.isArray(list) || list.length === 0) return true;
        const lr = list.find(
            (x: any) =>
                String(x?.Finance_Partner_Name ?? x?.finance_Partner_Name ?? '').trim() ===
                COMMON_CONSTANTS.LightreachPartnerName
        );
        if (!lr) return true;
        const status = String(
            lr?.Finance_Partner_Sync_Credit_Status ?? lr?.finance_Partner_Sync_Credit_Status ?? lr?.Credit_Status ?? ''
        ).trim();
        return status !== 'Declined';
    }, [opportunityDetails]);

    /** Mirrors Angular `OpportunityInfoComponent.showOnlyLightReachFinancingAlert` */
    const showOnlyLightReachFinancingAlert = useMemo(() => {
        if (leadSource !== 'TCD-LightReach') return false;
        if (!tcdLightReachFinancePartnerDropdownLogic) return false;

        const oppProducts =
            (opportunityDetails as any)?.OpportunityProduct ?? (opportunityDetails as any)?.opportunityProduct ?? [];
        const solarProd = Array.isArray(oppProducts)
            ? oppProducts.find((p: any) => matchProductName(p, COMMON_CONSTANTS.SolarProductName))
            : null;

        const solarPartner = String(
            solarProd?.Finance_Partner ??
                solarProd?.finance_Partner ??
                solarProd?.Finance_Partner_Display_Name ??
                solarProd?.FinancePartnerDisplayName ??
                ''
        ).trim();

        return (
            !solarPartner ||
            isLightreach(solarPartner) ||
            solarPartner === COMMON_CONSTANTS.LightreachPartnerName
        );
    }, [leadSource, opportunityDetails, tcdLightReachFinancePartnerDropdownLogic]);

    const isSolarLightReachSelected = useCallback((): boolean => {
        const solar = rows.find((r) => r.product_Type_Name === COMMON_CONSTANTS.SolarProductName);
        if (!solar?.activeFlag) return false;
        const p = String(solar.finance_Partner_Display_Name || '').trim();
        return !!p && isLightreach(p);
    }, [rows]);

    /** Angular `opportunity-info` only adds RR when `RR_Visibility === 'View'` */
    const rrProductLineAllowed = useMemo(
        () => getCapabilityValue('RR_Visibility') === 'View',
        [getCapabilityValue]
    );

    const hubProductOrder = useMemo(
        () =>
            dynamicProductOrder.filter(
                (name) => name !== COMMON_CONSTANTS.RRProduct || rrProductLineAllowed
            ),
        [dynamicProductOrder, rrProductLineAllowed]
    );

    const isSolarEnfinSelected = useCallback((): boolean => {
        const solar = rows.find((r) => r.product_Type_Name === COMMON_CONSTANTS.SolarProductName);
        if (!solar?.activeFlag) return false;
        const p = String(solar.finance_Partner_Display_Name || '').trim();
        return !!p && isEnfin(p);
    }, [rows]);

    const clearHubPartnerFields = (r: HubGridRow): HubGridRow => ({
        ...r,
        finance_Partner_Id: 0,
        finance_Partner: '',
        finance_Partner_Display_Name: 'Select Finance Partner',
        finance_Method_Name: '',
        methodOptions: [],
        methodsLoading: false,
    });

    const partnerOptionsForProduct = useCallback((productName: string) => {
        const enfinAllowed = enfinPartnerAllowedForDesignLosses(systemDesignLosses);
        const base = partnerOptions.filter(opt => {
            const partnerName = opt.value;
            const partnerLower = String(partnerName || '').toLowerCase();
            
            // Regional rule for Ohio
            if (isOHIO) {
                // if (partnerName === 'IGS' && productName === 'Solar') return false;
                if (isGoodLeap(partnerName) && productName !== 'Roof') return false;
            }

            // IGS logic (hidden entirely in LightReach-only mode — Angular shouldDisplayFinancePartner)
            if (partnerName === 'IGS') {
                const solar = rows.find(r => r.product_Type_Name === 'Solar');
                return (
                    !showOnlyLightReachFinancingAlert &&
                    productName === 'Solar' &&
                    !!solar?.activeFlag
                );
            }

            // Roof + Sunnova
            if (productName === 'Roof' && partnerName === 'Sunnova' && opportunityDetails?.isRoofFinancePartnerDisabled) {
                return false;
            }

            // Capability gating (mirrors Angular shouldDisplayFinancePartner)
            const isCash = partnerLower.includes('cash');
            const isService = partnerLower.includes('service');
            const isSunlight = partnerLower.includes('sunlight');

            if (productName === COMMON_CONSTANTS.SolarProductName) {
                if (showOnlyLightReachFinancingAlert) {
                    return isLightreach(partnerName) && getCapabilityValue('isLightreach_Solar') === 'View';
                }
                if (isService) return getCapabilityValue('isService_Finance_Solar') === 'View';
                if (isGoodLeap(partnerName)) return getCapabilityValue('isGoodLeap_Solar') === 'View';
                if (isSunlight) return getCapabilityValue('isSunlight_Solar') === 'View';
                if (isLightreach(partnerName)) return getCapabilityValue('isLightreach_Solar') === 'View';
                if (isEnfin(partnerName)) return getCapabilityValue('isEnfin_Solar') === 'View';
            }

            if (productName === COMMON_CONSTANTS.BatteryProductName) {
                if (showOnlyLightReachFinancingAlert) {
                    return (
                        isLightreach(partnerName) &&
                        getCapabilityValue('isLightreach_Battery') === 'View'
                    );
                }
                if (isCash) return true;
                if (isSunnova(partnerName)) return getCapabilityValue('Battery_Sunnova') === 'View';
                if (isService) return getCapabilityValue('isService_Finance_Battery') === 'View';
                if (isGoodLeap(partnerName)) return getCapabilityValue('isGoodLeap_Battery') === 'View';
                if (isSunlight) return getCapabilityValue('isSunlight_Battery') === 'View';
                if (isLightreach(partnerName)) {
                    return (
                        getCapabilityValue('isLightreach_Battery') === 'View' ||
                        getCapabilityValue('isLightReach_BatteryLease') === 'View'
                    );
                }
                if (isEnfin(partnerName)) return getCapabilityValue('isEnfin_Battery') === 'View' && isSolarEnfinSelected();
            }

            if (productName === COMMON_CONSTANTS.RRProduct) {
                if (isCash) return true;
                if (isService) return getCapabilityValue('isService_Finance_RR') === 'View';
                if (isGoodLeap(partnerName)) return getCapabilityValue('isGoodLeap_RR') === 'View';
                if (isSunlight) return getCapabilityValue('isSunlight_RR') === 'View';
                if (isLightreach(partnerName)) return getCapabilityValue('isLightreach_RR') === 'View';
                if (isEnfin(partnerName)) return getCapabilityValue('isEnfin_RR') === 'View';
            }

            if (productName === COMMON_CONSTANTS.RoofProductName || productName === COMMON_CONSTANTS.RRRProductName) {
                if (isService) {
                    // Angular checks both isService_Finance_RR and isService_Finance_RRR
                    return (
                        getCapabilityValue('isService_Finance_RR') === 'View' &&
                        getCapabilityValue('isService_Finance_RRR') === 'View'
                    );
                }
                if (isGoodLeap(partnerName)) {
                    return productName === COMMON_CONSTANTS.RoofProductName
                        ? getCapabilityValue('isGoodLeap_Roof') === 'View'
                        : getCapabilityValue('isGoodLeap_RRR') === 'View';
                }
                if (isSunlight) {
                    return productName === COMMON_CONSTANTS.RoofProductName
                        ? getCapabilityValue('isSunlight_Roof') === 'View'
                        : getCapabilityValue('isSunlight_RRR') === 'View';
                }
                if (isLightreach(partnerName)) {
                    return productName === COMMON_CONSTANTS.RoofProductName
                        ? getCapabilityValue('isLightreach_Roof') === 'View'
                        : getCapabilityValue('isLightreach_RRR') === 'View';
                }
                if (isEnfin(partnerName)) {
                    return productName === COMMON_CONSTANTS.RoofProductName
                        ? getCapabilityValue('isEnfin_Roof') === 'View'
                        : getCapabilityValue('isEnfin_RRR') === 'View';
                }
            }

            return true;
        });
        const withDisabledFlags = base.map((opt) => {
            const enfinDisabledByLosses =
                isEnfin(opt.value) &&
                !enfinAllowed &&
                productName === COMMON_CONSTANTS.SolarProductName;
            return {
                ...opt,
                disabled: enfinDisabledByLosses,
                disabledReason: enfinDisabledByLosses
                    ? ENFIN_PARTNER_REQUIRES_ENFIN_LOSSES_MESSAGE
                    : undefined,
            };
        });
        return [{ value: '', label: 'Select Finance Partner' }, ...withDisabledFlags];
    }, [
        partnerOptions,
        isOHIO,
        rows,
        opportunityDetails,
        getCapabilityValue,
        showOnlyLightReachFinancingAlert,
        isSolarLightReachSelected,
        isSolarEnfinSelected,
        systemDesignLosses,
    ]);

    const isProductDisabled = useCallback((name: string) => {
        const getActive = (n: string) => rows.find(r => r.product_Type_Name === n)?.activeFlag;
        const [isSolar, isRoof, isBattery, isRR, isRRR] = ['Solar', 'Roof', 'Battery', 'RR', 'RRR'].map(getActive);

        if (isRR && name !== 'RR') return true; // RR restricts all other products
        if ((isSolar || isRoof || isBattery || isRRR) && name === 'RR') return true; // Other products restrict RR
        if (name === 'Roof' && isRRR) return true; // Roof and RRR are mutually exclusive
        if (name === 'RRR' && isRoof) return true;

        return false;
    }, [rows]);


    /** Validates if the finance method dropdown should be interactive */
    const isMethodDropdownDisabled = useCallback((row: HubGridRow, allRows: HubGridRow[]) => {
        const partner = (row.finance_Partner_Display_Name || '').toLowerCase();
        if (!partner || partner === 'select finance partner' || partner.includes('cash')) return true;

        const isBatteryRow = row.product_Type_Name === COMMON_CONSTANTS.BatteryProductName;
        const isLightreachBattery = isBatteryRow && isLightreach(partner);

        // Lightreach Battery can have one method (Battery Lease) or two (Solar LR method + Battery Lease).
        if (!isLightreachBattery && row.methodOptions.length <= 1) return true;
        if (isLightreachBattery && row.methodOptions.length === 0) return true;

        if (isBatteryRow) {
            const solar = allRows.find(r => r.product_Type_Name === COMMON_CONSTANTS.SolarProductName);
            if (!solar?.activeFlag) return false;
            if (solar.methodsLoading) return true;

            const solarP = (solar.finance_Partner_Display_Name || '').toLowerCase();
            const solarMN = (solar.finance_Method_Name || '').toLowerCase();
            const [solarGL, solarEF] = [isGoodLeap(solarP), isEnfin(solarP)];
            
            const bothGL = solarGL && isGoodLeap(partner);
            const bothEF = solarEF && isEnfin(partner);
            const isStorageSync = solarMN.includes('storage') && (solarGL || isLightreach(solarP) || isSunnova(solarP));
            const isForcedBatteryLoan = isGoodLeap(partner) && (solarGL || isLightreach(solarP)) && ['lease', 'loan', 'ppa'].includes(solarMN);

            // V1: LR+LR Battery keeps method dropdown enabled (PPA vs Battery Lease).
            return bothGL || bothEF || isStorageSync || isForcedBatteryLoan;
        }

        if (row.methodOptions.length <= 1) return true;
        return false;
    }, []);

    const normalizePartnerForExcelPick = useCallback((selectedLabel: string, product: string): string => {
        const opts = partnerOptionsForProduct(product);
        const found = opts.find((o: any) => o.value === selectedLabel || o.label === selectedLabel);
        return found?.value ?? selectedLabel;
    }, [partnerOptionsForProduct]);

    const [focusedProduct, setFocusedProduct] = useState<string>('');
    const focusInitRef = useRef<string | null>(null);

    const [saveLoading, setSaveLoading] = useState(false);
    /** True while `fetchSvaFinancingMethodOptions` (Excel OutputCells E4) is in flight — blocks Save all product settings. */
    const [svaE4ExcelBusy, setSvaE4ExcelBusy] = useState(false);
    const [partnerModal, setPartnerModal] = useState<PartnerChecklistModalState | null>(null);

    const methodLoadSeqRef = useRef<Record<number, number>>({});
    /** Parallel `loadFinanceMethods` calls can overlap; re-enable Save only when all E4 requests finish. */
    const svaE4ExcelInflightRef = useRef(0);

    const opportunityId = opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? 0;
    const sfId = String(salesForceId ?? opportunityDetails?.SalesForce_Id ?? '').trim();

    const installStateCode = useMemo(() => {
        const a = opportunityDetails?.InstallationAddress;
        return String(a?.stateCode ?? a?.State ?? a?.state ?? 'CT').trim() || 'CT';
    }, [opportunityDetails]);

    const partnerDirectory = useMemo(() => {
        const byDisplay = new Map<string, { id: number; code: string; display: string }>();

        // 1. Populate from master data first to get reliable names and IDs (from GetMasterDataByStep)
        (financePartnersList || []).forEach((p: any) => {
            const name = String(p.Text || '').trim();
            if (!name) return;
            byDisplay.set(name.toLowerCase(), { id: Number(p.Value) || 0, code: name, display: name });
        });

        // 2. Merge with saved Opportunity products from the API
        const list = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        (Array.isArray(list) ? list : []).forEach((p: any) => {
            const d = String(
                p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.Finance_Partner ?? ''
            ).trim();
            if (!d) return;

            const id = Number(p.Finance_Partner_Id ?? p.FinancePartnerId ?? 0) || 0;
            const code = String(p.Finance_Partner ?? p.finance_Partner ?? d);
            const key = d.toLowerCase();

            if (byDisplay.has(key)) {
                const existing = byDisplay.get(key)!;
                // Prioritize the ID from master data as requested
                byDisplay.set(key, {
                    id: existing.id || id,
                    code: code || existing.code,
                    display: d,
                });
            } else {
                byDisplay.set(key, { id, code, display: d });
            }
        });

        return byDisplay;
    }, [opportunityDetails, financePartnersList]);

    const resolvePartnerIds = useCallback(
        (displayName: string) => {
            const key = String(displayName || '').trim().toLowerCase();
            if (!key) return { id: 0, code: '', display: '' };
            return partnerDirectory.get(key) ?? { id: 0, code: displayName, display: displayName };
        },
        [partnerDirectory]
    );

    const loadFinanceMethods = useCallback(
        async (index: number, rowSnapshot: HubGridRow, allRows: HubGridRow[]) => {
            const partnerName = String(rowSnapshot.finance_Partner_Display_Name || '').trim();
            const productName = rowSnapshot.product_Type_Name;

            if (!partnerName) {
                setRows((prev) =>
                    prev.map((r, i) => (i === index ? { ...r, methodOptions: [], finance_Method_Name: '', methodsLoading: false } : r))
                );
                return;
            }

            const isCashRow = partnerName.toLowerCase().includes('cash');
            const seq = (methodLoadSeqRef.current[index] = (methodLoadSeqRef.current[index] || 0) + 1);

            try {
                let methods: string[] = [];
                if (isRoofFamily(productName)) {
                    methods = await masterServices.fetchRoofFinancingMethodOptions(
                        opportunityDetails?.InstallationAddress?.stateCode || '',
                        roofProductCodeForC4(productName),
                        partnerName
                    );
                } else {
                    const solar = allRows.find((r) => r.product_Type_Name === COMMON_CONSTANTS.SolarProductName);
                    const battery = allRows.find((r) => r.product_Type_Name === COMMON_CONSTANTS.BatteryProductName);

                    // Solar + IGS does not use a finance method in this hub. Without this guard, a Battery + Good Leap
                    // line can drive notation "B" / GoodLeap API for every SVA row load and incorrectly fill Solar IGS.
                    if (productName === COMMON_CONSTANTS.SolarProductName) {
                        const p = String(partnerName || '').trim().toLowerCase();
                        if (p === 'igs' && !isCashRow) {
                            if (methodLoadSeqRef.current[index] !== seq) return;
                            setRows((prev) =>
                                prev.map((r, i) =>
                                    i === index ? { ...r, methodOptions: [], finance_Method_Name: '', methodsLoading: false } : r
                                )
                            );
                            return;
                        }
                    }

                    const [solarActive, batteryActive] = [!!solar?.activeFlag, !!battery?.activeFlag];
                    const [solarPartner, batteryPartner] = [solar?.finance_Partner_Display_Name || '', battery?.finance_Partner_Display_Name || ''];

                    // V1 getFinanceMethod: C4 is S/B/SB from selected partners, not "any solar selected".
                    const isMajorSolar = solarActive && (isLightreach(solarPartner) || isSunnova(solarPartner) || isGoodLeap(solarPartner) || isEnfin(solarPartner));
                    const isSvaBattery = batteryActive && (isGoodLeap(batteryPartner) || isLightreach(batteryPartner) || isEnfin(batteryPartner));
                    const partnersAreDifferent = !!(isMajorSolar && isSvaBattery && (
                        (solar?.finance_Partner_Id && battery?.finance_Partner_Id)
                            ? solar.finance_Partner_Id !== battery.finance_Partner_Id
                            : solarPartner.replace(/\s+/g, '').toLowerCase() !== batteryPartner.replace(/\s+/g, '').toLowerCase()
                    ));

                    const partnerLabel = (p: string) => {
                        if (isLightreach(p)) return COMMON_CONSTANTS.LightreachPartnerName;
                        if (isSunnova(p)) return COMMON_CONSTANTS.SunnovaPartnerName;
                        if (isGoodLeap(p)) return COMMON_CONSTANTS.GoodLeapPartnerName;
                        if (isEnfin(p)) return COMMON_CONSTANTS.EnfinPartnerName;
                        return p;
                    };

                    let notation = '';
                    let fPartnerName = '';
                    if (partnersAreDifferent) {
                        if (productName === COMMON_CONSTANTS.BatteryProductName) {
                            notation = 'B';
                            fPartnerName = partnerLabel(batteryPartner);
                        } else {
                            notation = 'S';
                            fPartnerName = partnerLabel(solarPartner);
                        }
                    } else {
                        if (isMajorSolar) notation += 'S';
                        if (isSvaBattery) notation += 'B';
                        if (!notation) notation = 'S';
                        fPartnerName = isMajorSolar ? partnerLabel(solarPartner) : (isSvaBattery ? partnerLabel(batteryPartner) : '');
                    }

                    // Special Rule: If the current loading row is CASH, ignore API results for it
                    if (!isCashRow) {
                        svaE4ExcelInflightRef.current += 1;
                        if (svaE4ExcelInflightRef.current === 1) setSvaE4ExcelBusy(true);
                        try {
                            methods = await masterServices.fetchSvaFinancingMethodOptions(
                                opportunityDetails?.InstallationAddress?.stateCode || '',
                                notation,
                                fPartnerName
                            );
                            if (isLightreach(fPartnerName) && getCapabilityValue('isLightReach_BatteryLease') !== 'View') {
                                methods = methods.filter((m) => String(m).trim() !== 'Battery Lease');
                            }
                            if (productName === COMMON_CONSTANTS.SolarProductName && isLightreach(fPartnerName) && isMajorSolar) {
                                methods = methods.filter((m) => String(m).trim() !== 'Battery Lease');
                            }
                        } finally {
                            svaE4ExcelInflightRef.current -= 1;
                            if (svaE4ExcelInflightRef.current === 0) setSvaE4ExcelBusy(false);
                        }
                    }

                    // Add 'Battery Loan' if GoodLeap is used for Battery and conditions match
                    if (productName === COMMON_CONSTANTS.BatteryProductName && isGoodLeap(partnerName)) {
                        const isLeaseLoanPPA = ['Lease', 'Loan', 'PPA'].includes(solar?.finance_Method_Name || '');
                        if (isLeaseLoanPPA && !methods.includes('Battery Loan')) {
                            methods.unshift('Battery Loan');
                        }
                    }

                    if (productName === COMMON_CONSTANTS.BatteryProductName && isLightreach(partnerName)) {
                        methods = resolveLightreachBatteryMethodOptions(
                            methods,
                            allRows,
                            getCapabilityValue('isLightReach_BatteryLease') === 'View',
                        );
                    }
                }

                // Capability filtering for GoodLeap solar finance methods (mirrors Angular getFinanceMethod filter)
                if (productName === COMMON_CONSTANTS.SolarProductName && isGoodLeap(partnerName) && Array.isArray(methods)) {
                    const allow = (capName: string) => getCapabilityValue(capName) === 'View';
                    methods = methods.filter((m: string) => {
                        const t = String(m || '').trim().toLowerCase();
                        if (t === 'loan') return allow('isGoodLeap_Solar_Loan');
                        if (t === 'lease') return allow('isGoodLeap_Solar_Lease');
                        if (t === 'ppa') return allow('isGoodLeap_Solar_PPA');
                        if (t === 'loan storage') return allow('isGoodLeap_Solar_LoanStorage');
                        if (t === 'lease storage') return allow('isGoodLeap_Solar_LeaseStorage');
                        if (t === 'ppa storage') return allow('isGoodLeap_Solar_PPAStorage');
                        return true;
                    });
                }

                // Storage finance methods are only valid when Battery is included.
                // Even if the SVA API returns them (stale notation / race), hide them for Solar-only.
                if (productName === COMMON_CONSTANTS.SolarProductName && Array.isArray(methods)) {
                    const batteryRowForFilter = allRows.find(
                        (r) => r.product_Type_Name === COMMON_CONSTANTS.BatteryProductName,
                    );
                    if (!batteryRowForFilter?.activeFlag) {
                        methods = methods.filter((m: string) => !isStorageFinanceMethod(m));
                    }
                }

                if (methodLoadSeqRef.current[index] !== seq) return;

                setRows((prev) => {
                    const nextRows = prev.map((r, i) => {
                    if (i !== index) return r;

                    // The row can be deactivated or have its partner cleared while this lookup is in flight
                    // (e.g. unselecting the product). Those paths start no new load, so the sequence guard
                    // above cannot catch them — drop the response instead of restoring a stale method.
                    const currentPartner = String(r.finance_Partner_Display_Name || '').trim();
                    if (!r.activeFlag || currentPartner.toLowerCase() !== partnerName.toLowerCase()) {
                        return r;
                    }

                    let bestMethod = r.finance_Method_Name;
                    const oppProds = opportunityDetails?.OpportunityProduct || [];
                    const savedProd = oppProds.find((op: any) => matchProductName(op, r.product_Type_Name) && op.ActiveFlag);
                    const savedMethod = savedProd?.Finance_Method_Name;
                    const savedPartner = String(
                        savedProd?.Finance_Partner_Display_Name ??
                            savedProd?.FinancePartnerDisplayName ??
                            savedProd?.Finance_Partner ??
                            savedProd?.finance_Partner ??
                            ''
                    ).trim();

                    const solarRow = prev.find((x) => x.product_Type_Name === COMMON_CONSTANTS.SolarProductName);
                    const batteryRow = prev.find((x) => x.product_Type_Name === COMMON_CONSTANTS.BatteryProductName);
                    const solarMN = solarRow?.finance_Method_Name || '';
                    const solarP = solarRow?.finance_Partner_Display_Name || '';
                    const s_isLeaseLoanPPAValue = ['lease', 'loan', 'ppa'].includes(solarMN.toLowerCase());

                    if (r.product_Type_Name === COMMON_CONSTANTS.BatteryProductName && isGoodLeap(partnerName)) {
                        if (solarRow?.activeFlag && s_isLeaseLoanPPAValue && (isGoodLeap(solarP) || isLightreach(solarP))) {
                            bestMethod = 'Battery Loan';
                        }
                    } else if (r.product_Type_Name === COMMON_CONSTANTS.BatteryProductName && isLightreach(partnerName)) {
                        const solarIsLr = !!(solarRow?.activeFlag && isLightreach(solarP));
                        if (!bestMethod || !methods.includes(bestMethod)) {
                            if (solarIsLr && solarMN && methods.includes(solarMN)) {
                                bestMethod = solarMN;
                            } else if (methods.includes('Battery Lease')) {
                                bestMethod = 'Battery Lease';
                            }
                        }
                    } else if (r.product_Type_Name === COMMON_CONSTANTS.BatteryProductName && isCashRow) {
                        bestMethod = '';
                    }

                    if (isRoofFamily(r.product_Type_Name)) {
                        if (solarRow && solarRow.finance_Partner_Display_Name === partnerName) {
                            if (methods.includes(solarRow.finance_Method_Name || '')) {
                                bestMethod = solarRow.finance_Method_Name;
                            }
                        }
                    }

                    if (methods.length > 0) {
                        if (isGoodLeap(r.finance_Partner_Display_Name) && r.finance_Partner_Display_Name && methods.includes(r.finance_Method_Name)) {
                            bestMethod = r.finance_Method_Name;
                        } else if (bestMethod === 'Battery Loan') {
                            // Keep
                        } else if (
                            savedPartner &&
                            partnerName &&
                            savedPartner.toLowerCase() === partnerName.toLowerCase() &&
                            savedMethod &&
                            methods.includes(savedMethod)
                        ) {
                            bestMethod = savedMethod;
                        } else if (bestMethod && methods.includes(bestMethod)) {
                            // Keep
                        } else if (
                            savedPartner &&
                            partnerName &&
                            savedPartner.toLowerCase() === partnerName.toLowerCase() &&
                            savedMethod &&
                            !methods.includes(savedMethod)
                        ) {
                            bestMethod = '';
                        }else if (methods.length === 1) {
                            bestMethod = methods[0];
                        }else {
                            bestMethod =  methods[0];
                        }
                    } else {
                        bestMethod = '';
                    }

                    // If Battery is off, never keep a Storage method on Solar (e.g. prior PPA Storage).
                    if (
                        r.product_Type_Name === COMMON_CONSTANTS.SolarProductName &&
                        isStorageFinanceMethod(bestMethod) &&
                        !batteryRow?.activeFlag
                    ) {
                        const nonStorage = stripStorageFromFinanceMethod(bestMethod);
                        bestMethod = methods.includes(nonStorage) ? nonStorage : methods[0] || '';
                    }

                    const bothGL = !!(solarRow && batteryRow && isGoodLeap(solarRow.finance_Partner_Display_Name) && isGoodLeap(batteryRow.finance_Partner_Display_Name));

                    if(bothGL){
                        if(batteryRow && batteryRow.finance_Method_Name === 'Battery Loan' ){
                           if (r.product_Type_Name === COMMON_CONSTANTS.SolarProductName) {
                               bestMethod = methods.includes(bestMethod) ? bestMethod : '';
                           }
                        } 
                    }

                    return { ...r, methodOptions: methods, finance_Method_Name: bestMethod, methodsLoading: false };
                });

                    return nextRows;
                });
            } catch (err) {
                console.error('Failed to load finance methods:', err);
            } finally {
                if (methodLoadSeqRef.current[index] === seq) {
                    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, methodsLoading: false } : r)));
                }
            }
        },
        [opportunityDetails, productTypeList, getCapabilityValue]
    );

    const rowsRef = useRef<HubGridRow[]>([]);
    useEffect(() => { rowsRef.current = rows; }, [rows]);

    const opportunitySyncKeyRef = useRef<string>('');

    const applySavedOpportunityProductsToHub = useCallback(
        (options?: { force?: boolean }) => {
            const force = options?.force === true;
            const productOrder =
                dynamicProductOrder.length > 0
                    ? dynamicProductOrder
                    : ['Solar', 'Roof', 'Battery', 'RR', 'RRR'];

            const blankRows = () =>
                productOrder.map((pt) => ({
                    ...rowFromApi(undefined, pt),
                    methodOptions: [],
                    methodsLoading: false,
                }));

            if (!opportunityDetails) {
                if (force || opportunitySyncKeyRef.current || rowsRef.current.length === 0) {
                    opportunitySyncKeyRef.current = '';
                    setRows(blankRows());
                }
                return;
            }

            const raw = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
            const arr = Array.isArray(raw) ? raw : [];
            const prevRows = rowsRef.current;
            const syncKey = [
                String(opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? ''),
                String(sfId || ''),
                productOrder.join('|'),
                partnerOptions.map((o) => o.value).join('|'),
                savedOpportunityProductsFingerprint(arr),
            ].join('::');
            if (!force && syncKey === opportunitySyncKeyRef.current && prevRows.length > 0) return;

            const hasDraftEdits =
                !hubRowsLookLikePlaceholders(prevRows) &&
                prevRows.some((row) => hubRowHasUnsavedChanges(row, arr));
            if (!force && hasDraftEdits) {
                opportunitySyncKeyRef.current = syncKey;
                return;
            }

            opportunitySyncKeyRef.current = syncKey;

            const next: HubGridRow[] = productOrder.map((pt) => {
                const found = arr.find((p: any) => matchProductName(p, pt));
                const base = rowFromApi(found, pt);
                let display = base.finance_Partner_Display_Name;

                if (pt === COMMON_CONSTANTS.BatteryProductName && display && isSunnova(display)) {
                    if (getCapabilityValue('Battery_Sunnova') !== 'View') {
                        display = '';
                    }
                }

                if (display && partnerOptions.every((o) => o.value !== display)) {
                    const match = partnerOptions.find(
                        (o) =>
                            o.value.toLowerCase() === display.toLowerCase() ||
                            display.toLowerCase().includes(o.value.toLowerCase().slice(0, 4)),
                    );
                    if (match) display = match.value;
                }
                const ids = resolvePartnerIds(display);
                const newDisplayName = display || '';

                const ex = prevRows.find((r) => r.product_Type_Name === pt);
                const samePartner = !force && ex && ex.finance_Partner_Display_Name === newDisplayName;

                return {
                    ...base,
                    product_Type_Name: pt,
                    finance_Partner_Display_Name: newDisplayName,
                    finance_Partner_Id: ids.id || base.finance_Partner_Id,
                    finance_Partner: ids.code || base.finance_Partner,
                    methodOptions: samePartner ? ex.methodOptions : [],
                    methodsLoading: samePartner ? ex.methodsLoading : !!newDisplayName,
                };
            });
            setRows(next);
            next.forEach((row, i) => {
                if (!row.finance_Partner_Display_Name) return;
                const ex = prevRows.find((r) => r.product_Type_Name === row.product_Type_Name);
                const partnerChanged = ex?.finance_Partner_Display_Name !== row.finance_Partner_Display_Name;
                const activeToggled = ex?.activeFlag !== row.activeFlag;
                if (force || partnerChanged || activeToggled) {
                    loadFinanceMethods(i, { ...row, methodOptions: [], methodsLoading: true }, next);
                }
            });

            const key = sfId || String(opportunityId);
            if (force || focusInitRef.current !== key) {
                focusInitRef.current = key;
                const firstActive = hubProductOrder.find((n) => next.find((x) => x.product_Type_Name === n)?.activeFlag);
                setFocusedProduct(firstActive ?? hubProductOrder[0] ?? '');
            }
        },
        [
            opportunityDetails,
            resolvePartnerIds,
            loadFinanceMethods,
            sfId,
            opportunityId,
            dynamicProductOrder,
            partnerOptions,
            getCapabilityValue,
            hubProductOrder,
        ],
    );

    useEffect(() => {
        applySavedOpportunityProductsToHub();
    }, [applySavedOpportunityProductsToHub]);

    const resetHubRowsFromSavedOpportunity = useCallback(() => {
        setValidationError(null);
        setValidationErrorsByProduct({});
        applySavedOpportunityProductsToHub({ force: true });
    }, [applySavedOpportunityProductsToHub]);

    const productsModalOpenRef = useRef(productsModalOpen);
    useEffect(() => {
        const wasOpen = productsModalOpenRef.current;
        productsModalOpenRef.current = productsModalOpen;
        if (wasOpen === productsModalOpen) return;
        resetHubRowsFromSavedOpportunity();
    }, [productsModalOpen, resetHubRowsFromSavedOpportunity]);

    const getRow = useCallback(
        (productName: string) => {
            const exact = rows.find((r) => r.product_Type_Name === productName);
            if (exact) return exact;
            // Card embeds may pass display titles; map roof-family aliases to hub rows.
            const lower = String(productName || '').trim().toLowerCase();
            if (lower === 'rrr' || lower.includes('roof')) {
                return (
                    rows.find((r) => r.product_Type_Name === 'RRR') ||
                    rows.find((r) => r.product_Type_Name === 'Roof') ||
                    rows.find((r) => r.product_Type_Name === 'RR') ||
                    undefined
                );
            }
            if (lower.includes('battery')) {
                return rows.find((r) => r.product_Type_Name === 'Battery');
            }
            return undefined;
        },
        [rows]
    );

    const clearProductValidationError = useCallback((productName: string) => {
        setValidationErrorsByProduct((prev) => {
            if (!prev[productName]) return prev;
            const next = { ...prev };
            delete next[productName];
            return next;
        });
    }, []);

    const getProductValidationError = useCallback(
        (productName: string) => validationErrorsByProduct[productName] ?? null,
        [validationErrorsByProduct],
    );

    const setPartnerForProduct = useCallback(
        (productName: string, partnerValue: string, options?: { activate?: boolean }) => {
            if (useOneButtonStore.getState().pciLockOpportunityProducts) return;
            const idx = rows.findIndex((r) => r.product_Type_Name === productName);
            if (idx < 0) return;
            const label = normalizePartnerForExcelPick(partnerValue, productName);
            if (
                isEnfin(label) &&
                productName === COMMON_CONSTANTS.SolarProductName &&
                !enfinPartnerAllowedForDesignLosses(systemDesignLosses)
            ) {
                noticeAPI.warning({
                    message: 'Enfin not available',
                    description: ENFIN_PARTNER_REQUIRES_ENFIN_LOSSES_MESSAGE,
                    placement: 'topRight',
                    className: `obd-notification obd-theme-${theme}`,
                });
                return;
            }
            const ids = resolvePartnerIds(label);
            setValidationError(null);
            clearProductValidationError(productName);

            const currentRow = rows[idx];
            const shouldActivate = options?.activate === true || currentRow.activeFlag;

            const snap: HubGridRow = {
                ...currentRow,
                activeFlag: shouldActivate,
                finance_Partner_Display_Name: label,
                finance_Partner_Id: ids.id,
                finance_Partner: ids.code || label,
                finance_Method_Name: '',
                methodOptions: [],
                methodsLoading: true,
            };

            let next = rows.map((r, i) => (i === idx ? snap : r));

            if (options?.activate && isRoofFamily(productName)) {
                const family = ['Roof', 'RR', 'RRR'];
                next = next.map((r) =>
                    family.includes(r.product_Type_Name) && r.product_Type_Name !== productName
                        ? { ...r, activeFlag: false }
                        : r
                );
            }

            // A partner change updates only this product. Other selected products keep their partner and method.
            setRows(next);

            loadFinanceMethods(idx, snap, next);
        },
        [rows, resolvePartnerIds, loadFinanceMethods, normalizePartnerForExcelPick, clearProductValidationError, systemDesignLosses, noticeAPI, theme]
    );

    const setMethodForProduct = useCallback((productName: string, method: string) => {
        if (useOneButtonStore.getState().pciLockOpportunityProducts) return;
        clearProductValidationError(productName);
        setRows((prev) => {
            let next = prev.map((r) =>
                r.product_Type_Name === productName ? { ...r, finance_Method_Name: method } : r
            );
            setValidationError(null);

            // Sync Logic: Bidirectional Solar/Battery/Roof Sync
            const isSvaProduct = productName === COMMON_CONSTANTS.SolarProductName || productName === COMMON_CONSTANTS.BatteryProductName;
            const updatedRow = next.find(r => r.product_Type_Name === productName);
            
            if (updatedRow && updatedRow.activeFlag) {
                const updatedPartner = (updatedRow.finance_Partner_Display_Name || '').toLowerCase();
                const updatedMethod = (method || '').toLowerCase();
                // Parity with shouldShowBatteryLoan line 1319 (Exact matching for Lease/Loan/PPA)
                const isLeaseLoanPPA_Exact = ['lease', 'loan', 'ppa'].includes(updatedMethod);

                next = next.map(r => {
                    const rName = r.product_Type_Name;
                    if (rName === productName || !r.activeFlag) return r;

                    const rPartner = (r.finance_Partner_Display_Name || '').toLowerCase();
                    const sharedPartner = updatedPartner === rPartner && updatedPartner !== '';

                    // Priority Check: Solar GL Reset (Exactly as requested)
                    if (rName === COMMON_CONSTANTS.SolarProductName && isGoodLeap(rPartner)) {
                        const bRow = next.find(x => x.product_Type_Name === COMMON_CONSTANTS.BatteryProductName);
                        if (bRow && isGoodLeap(bRow.finance_Partner_Display_Name) && bRow.finance_Method_Name === 'Battery Loan') {
                            return { ...r, finance_Method_Name: r.methodOptions[0] || '' };
                        }
                    }

                    // 1. Solar <-> Battery synchronization
                    if (isSvaProduct && (rName === COMMON_CONSTANTS.SolarProductName || rName === COMMON_CONSTANTS.BatteryProductName)) {
                        
                        // Rule A: GoodLeap Battery Forced State (Exclusively for exact Lease/Loan/PPA)
                        if (rName === COMMON_CONSTANTS.BatteryProductName && isGoodLeap(rPartner) && isLeaseLoanPPA_Exact && (isGoodLeap(updatedPartner) || isLightreach(updatedPartner))) {
                            return { ...r, finance_Method_Name: 'Battery Loan' };
                        }

                        // Rule B: Generic sync if partners match (Syncs storage methods like 'Lease Storage' exactly)
                        if (sharedPartner && (r.methodOptions.includes(method) || r.finance_Method_Name === method || updatedMethod.includes('storage'))) {
                            return { ...r, finance_Method_Name: method };
                        }
                    }

                    // 2. Roof family synchronization (Sync with Solar)
                    if (productName === COMMON_CONSTANTS.SolarProductName && isRoofFamily(rName)) {
                        if (sharedPartner && r.methodOptions.includes(method)) {
                            return { ...r, finance_Method_Name: method };
                        }
                    }

                    return r;
                });
            }
            return next;
        });
    }, [clearProductValidationError]);

    const toggleProductIncluded = useCallback(
        (name: string, included: boolean) => {
            setValidationError(null);
            clearProductValidationError(name);
            setRows((prev) => {
                let next = prev.map((r) => {
                    if (r.product_Type_Name === name) {
                        let finalPartner = included ? r.finance_Partner_Display_Name : '';
                        let finalPartnerId = included ? r.finance_Partner_Id : 0;
                        let finalPartnerCode = included ? r.finance_Partner : '';

                        // GoodLeap Default Rule: If Solar is selected and has no partner, default to GoodLeap if approved
                        if (included && name === COMMON_CONSTANTS.SolarProductName && !finalPartner && isGoodLeapApproved) {
                            finalPartner = COMMON_CONSTANTS.GoodLeapPartnerName;
                            const ids = resolvePartnerIds(finalPartner);
                            finalPartnerId = ids.id;
                            finalPartnerCode = ids.code;
                        }

                        return { 
                            ...r, 
                            activeFlag: included,
                            finance_Partner_Display_Name: finalPartner,
                            finance_Partner_Id: finalPartnerId,
                            finance_Partner: finalPartnerCode,
                            finance_Method_Name: included ? r.finance_Method_Name : '',
                        };
                    }
                    return r;
                });
                
                // Solar off: clear dependents only when tied to Solar (Enfin battery; LR when Solar was LR or TCD-only).
                if (name === COMMON_CONSTANTS.SolarProductName && !included) {
                    const solarPartnerBefore = String(
                        prev.find((r) => r.product_Type_Name === COMMON_CONSTANTS.SolarProductName)
                            ?.finance_Partner_Display_Name ?? '',
                    ).trim();

                    next = next.map((r) => {
                        if (r.product_Type_Name === name) return r;
                        const partner = r.finance_Partner_Display_Name || '';

                        if (
                            r.product_Type_Name === COMMON_CONSTANTS.BatteryProductName &&
                            isEnfin(partner)
                        ) {
                            return clearHubPartnerFields(r);
                        }

                        if (showOnlyLightReachFinancingAlert && isLightreach(partner)) {
                            return clearHubPartnerFields(r);
                        }

                        // e.g. Solar Good Leap + Battery Lightreach — keep Battery partner/method.
                        if (
                            r.product_Type_Name === COMMON_CONSTANTS.BatteryProductName &&
                            isLightreach(partner) &&
                            !isLightreach(solarPartnerBefore)
                        ) {
                            return r;
                        }

                        // Solar LR off + Battery LR: keep Lightreach partner; reset method for Battery Lease (V1).
                        if (
                            r.product_Type_Name === COMMON_CONSTANTS.BatteryProductName &&
                            isLightreach(solarPartnerBefore) &&
                            isLightreach(partner)
                        ) {
                            return {
                                ...r,
                                finance_Method_Name: '',
                                methodOptions: [],
                                methodsLoading: true,
                            };
                        }

                        return r;
                    });
                }

                // If Battery is deselected, Solar can no longer use Storage methods (PPA Storage → PPA, etc.).
                if (name === COMMON_CONSTANTS.BatteryProductName && !included) {
                    next = next.map((r) => {
                        if (
                            r.product_Type_Name === COMMON_CONSTANTS.SolarProductName &&
                            isStorageFinanceMethod(r.finance_Method_Name)
                        ) {
                            return {
                                ...r,
                                finance_Method_Name: stripStorageFromFinanceMethod(r.finance_Method_Name),
                            };
                        }
                        return r;
                    });
                }

                // Mutual exclusion for Roof Family (Roof, RR, RRR)
                if (included && isRoofFamily(name)) {
                    const family = ['Roof', 'RR', 'RRR'];
                    next = next.map((r) => family.includes(r.product_Type_Name) && r.product_Type_Name !== name ? { ...r, activeFlag: false } : r);
                }

                // Refresh finance methods for family members (SVA or Roof) if they changed
                const isSvaFamily = name === 'Solar' || name === 'Battery';
                if (isSvaFamily || isRoofFamily(name)) {
                    // We need to pass the updated 'next' state to loadFinanceMethods
                    ['Solar', 'Battery', 'Roof', 'RR', 'RRR'].forEach((p) => {
                        const idx = next.findIndex((r) => r.product_Type_Name === p);
                        if (idx > -1 && next[idx].activeFlag && next[idx].finance_Partner_Display_Name) {
                            // Only trigger if it's the one that changed, or if it's SVA and we need to refresh notation
                            const shouldReload =
                                p === name ||
                                (isSvaFamily && (p === 'Solar' || p === 'Battery'));
                            
                            if (shouldReload) {
                                loadFinanceMethods(idx, next[idx], next);
                            }
                        }
                    });
                }

                return next;
            });
        },
        [loadFinanceMethods, showOnlyLightReachFinancingAlert, isGoodLeapApproved, clearProductValidationError]
    );

    const hasExistingGoodLeapApp = useCallback((ProductTypeName: string) => {
        const list = opportunityDetails?.FinancePartnerLeadList ?? opportunityDetails?.financePartnerLeadList;
        if (!Array.isArray(list)) return undefined;
        return list.find((item: any) => (item.ProductTypeName === ProductTypeName && item.FinancePartnerName === COMMON_CONSTANTS.GoodLeapPartnerName && item.Finance_Partner_Sync_LeadID));
    }, [opportunityDetails]);

    const handleProductChange = useCallback((name: string, included: boolean) => {
        if (useOneButtonStore.getState().pciLockOpportunityProducts) return;
        setGoodLeapExistingApp(null)
        if (!included && (name === COMMON_CONSTANTS.SolarProductName || name === COMMON_CONSTANTS.BatteryProductName)) {
            const existingApp = hasExistingGoodLeapApp(name);
            if (existingApp) {
                setGoodLeapExistingApp(existingApp)
            } else toggleProductIncluded(name, included);
        } else {
            toggleProductIncluded(name, included);
        }
        
    }, [hasExistingGoodLeapApp, toggleProductIncluded]);

    const persistOpportunityProducts = useCallback(
        async (opportunityProduct: ReturnType<typeof buildOpportunityProductSavePayload>, successMessage: string) => {
            const response = await opportunityInfoServices.updateOpportunityProduct({
                opportunityId: +opportunityId,
                salesForce_Id: sfId,
                opportunityProduct,
            });

            noticeAPI.success({
                message: 'Success',
                description: response?.Message || response?.message || successMessage,
                placement: 'topRight',
                className: `obd-notification obd-theme-${theme}`,
            });

            const refreshed = await opportunityInfoServices.getOpportunityDetail(sfId);
            await quoteServices.contractGetLoanStatusByOpportunity(opportunityId);

            const detailData = refreshed?.data ?? refreshed;
            const merged = mergeSavedProductsIntoOpportunityDetails(
                detailData ?? useOneButtonStore.getState().opportunityDetails,
                opportunityProduct,
            );
            if (merged) setOpportunityDetails(merged);

            setFinanceSelectionRefreshTrigger(Date.now());

            try {
                const numericOppId = Number(detailData?.Opportunity_Id ?? opportunityId) || 0;
                if (numericOppId) {
                    const utilityResult = await designServices.RetrieveUtilityInformation(numericOppId);
                    setSolarUtilityData(utilityResult?.utilityDetails ?? null);
                } else {
                    setSolarUtilityData(null);
                }
            } catch {
                setSolarUtilityData(null);
            }
        },
        [
            opportunityId,
            sfId,
            noticeAPI,
            theme,
            setOpportunityDetails,
            setFinanceSelectionRefreshTrigger,
            setSolarUtilityData,
        ],
    );

    const runProductFinanceSave = useCallback(
        async (scopeProductName?: string) => {
            if (useOneButtonStore.getState().pciLockOpportunityProducts) {
                noticeAPI.warning({
                    message: 'Finalization pending',
                    description:
                        'Finalize the oneDRAW design before changing opportunity products or finance partners.',
                    placement: 'topRight',
                    className: `obd-notification obd-theme-${theme}`,
                });
                return;
            }
            if (!opportunityId || !sfId) {
                noticeAPI.error({
                    message: 'Error',
                    description: 'Opportunity data is not loaded.',
                    placement: 'topRight',
                    className: `obd-notification obd-theme-${theme}`,
                });
                return;
            }

            const rowsSnapshot = rowsRef.current.length > 0 ? rowsRef.current : rows;
            const validationMessage = validateProductFinanceForSave(rowsSnapshot, scopeProductName);
            if (validationMessage) {
                if (scopeProductName) {
                    setValidationErrorsByProduct((prev) => ({ ...prev, [scopeProductName]: validationMessage }));
                } else {
                    setValidationError(validationMessage);
                }
                return;
            }

            
            const res: any = await designServices.SystemDesignLosses(String(opportunityId));
            const lossesMismatch = evaluateDesignLossesMismatchForHubSave(rowsSnapshot, res?.LossesSource);

            if (lossesMismatch.blocked) {
                const mismatchMessage =
                    lossesMismatch.message ?? 'Design losses do not match the selected finance partner(s).';
                if (scopeProductName) {
                    setValidationErrorsByProduct((prev) => ({
                        ...prev,
                        [scopeProductName]: mismatchMessage,
                    }));
                } else {
                    setValidationError(mismatchMessage);
                }
                showDesignLossesMismatchModal(mismatchMessage, theme);
                return;
            }

            await runProductFinanceSaveAction(scopeProductName);

           
        },
        [
            opportunityId,
            sfId,
            rows,
            opportunityDetails,
            productTypeList,
            noticeAPI,
            theme,
            persistOpportunityProducts,
            clearProductValidationError,
        ],
    );

    const runProductFinanceSaveAction = async (scopeProductName?: string) => {
        if (scopeProductName) {
            clearProductValidationError(scopeProductName);
        } else {
            setValidationError(null);
            setValidationErrorsByProduct({});
        }

        const originalRaw = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        const originalArr = Array.isArray(originalRaw) ? originalRaw : [];
        const rowsSnapshot = rowsRef.current.length > 0 ? rowsRef.current : rows;
        const opportunityProduct = buildOpportunityProductSavePayload(
            rowsSnapshot,
            originalArr,
            opportunityId,
            productTypeList ?? [],
            scopeProductName,
        );

        const successMessage = scopeProductName
            ? `${scopeProductName} settings saved successfully.`
            : 'Products saved successfully.';

        if (scopeProductName) {
            setSaveLoadingProduct(scopeProductName);
        } else {
            setSaveLoading(true);
        }

        const solarPartnerChanged =
            !scopeProductName && solarFinancePartnerChanged(rows, originalArr);

        try {
            await persistOpportunityProducts(opportunityProduct, successMessage);
            if (solarPartnerChanged) {
                packingSlipService.fetchFinancePartnerDetails().then((details) => {
                    useViewerStore.setState({ financePartnerDetails: details });
                });
            }
        } catch (e: any) {
            noticeAPI.error({
                message: 'Error',
                description:
                    e?.response?.data?.Message || e?.message || 'Failed to save product settings.',
                placement: 'topRight',
                className: `obd-notification obd-theme-${theme}`,
            });
        } finally {
            if (scopeProductName) {
                setSaveLoadingProduct(null);
            } else {
                setSaveLoading(false);
            }
        }
    }

    /** Save all product settings to the backend */
    const handleSaveProducts = useCallback(async () => {
        await runProductFinanceSave();
    }, [runProductFinanceSave]);

    /** Save a single product line from an embedded system-design card */
    const handleSaveProduct = useCallback(
        async (productName: string) => {
            await runProductFinanceSave(productName);
        },
        [runProductFinanceSave],
    );


    const menuPopupClass =
        theme === 'light' ? 'obd-pf-menu-popup obd-pf-menu-popup--light' : 'obd-pf-menu-popup obd-pf-menu-popup--dark';

    return {
        theme,
        salesForceId,
        rows,
        hubProductOrder,
        getRow,
        handleProductChange,
        setPartnerForProduct,
        setMethodForProduct,
        partnerOptionsForProduct,
        isProductDisabled,
        isMethodDropdownDisabled,
        isOpportunityInfoReadOnly,
        isOpportunityInfoDisabled,
        getCapabilityValue,
        hasUnsavedChanges,
        hasProductUnsavedChanges,
        hasSavedFinancePartnerForProduct,
        resetHubRowsFromSavedOpportunity,
        noticeAPI,
        noticeContext,
        setLeadSyncFormOpen,
        setPartnerModal,
        menuPopupClass,
        installStateCode,
        saveLoading,
        saveLoadingProduct,
        svaE4ExcelBusy,
        validationError,
        getProductValidationError,
        handleSaveProducts,
        handleSaveProduct,
        opportunityId,
        partnerModal,
        goodLeapExistingApp,
        setGoodLeapExistingApp,
        toggleProductIncluded,
        hubOpen,
        setHubOpen,
        opportunityLoadError,
        isOpportunityInitializing,
    };
}